/**
 * 未完成区間（UnfinishedSections）管理ユーティリティ
 *
 * Issue #25: 線路の方向転換（redirect）機能の実装に伴う再設計
 *
 * 従来の UnfinishedSection はタイル単位（path: HexCoordinate[]）で管理していたが、
 * 線路単位（coord, entryEdge, exitEdge）での開放端管理に変更。
 *
 * @see docs/issues/issue-25-track-redirection.md
 */

import type {
  AoSGameState,
  HexCoordinate,
  TrackTile,
  UnfinishedSection,
  Direction,
  PlayerId
} from '../../types';
import {
  coordToKey,
  getAdjacentHex,
  getOppositeDirection
} from '../../types';
import { debug } from '../../utils/debug';
import { getTileDefinition } from '../constants/tiles';

/**
 * 開放端情報
 */
interface OpenEndInfo {
  /** 入口の辺番号（接続済み側） */
  entryEdge: Direction;
  /** 出口の辺番号（開放端側） */
  exitEdge: Direction;
  /** 複線タイルの場合の経路インデックス */
  connectionIndex?: number;
}

/**
 * 隣接ヘクスが「接続済み」かどうかを判定
 *
 * ## 接続判定ルール
 *
 * | ヘクス種別      | タイルなし | タイルあり（対応辺に接続あり） | タイルあり（対応辺に接続なし） |
 * |----------------|-----------|------------------------------|------------------------------|
 * | City（都市）    | ✅ 接続   | ✅ 接続                       | ✅ 接続                       |
 * | Town（町）      | ❌ 未接続 | ✅ 接続                       | ❌ 未接続                     |
 * | 空きヘクス      | ❌ 未接続 | ✅ 接続                       | ❌ 未接続                     |
 * | マップ外        | ❌ 未接続 | -                            | -                            |
 *
 * ## 重要な仕様
 *
 * - **City（都市）は終端点**: タイルがなくても接続先として機能する
 * - **Town（町）は経由点**: タイルが配置されて初めて接続が成立する
 *   - Issue #27 で発見: Town をタイル不要で接続済みとすると、
 *     複線タイルの開放端が誤って「完成」と判定される問題が発生
 *
 * @param G - ゲーム状態
 * @param coord - 判定元の座標
 * @param direction - 判定する方向（0-5）
 * @returns 接続済みなら true
 *
 * @see docs/issues/issue-27-double-track-unfinished-sections.md
 */
function isEdgeConnected(
  G: AoSGameState,
  coord: HexCoordinate,
  direction: Direction
): boolean {
  const adjacentCoord = getAdjacentHex(coord, direction);
  const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === coordToKey(adjacentCoord));

  if (!adjacentHex) {
    // マップ外 = 接続不可
    debug.log(`[isEdgeConnected] ${coordToKey(coord)} dir=${direction} → ${coordToKey(adjacentCoord)} = OFF MAP`);
    return false;
  }

  // 都市があれば接続済み（都市は終端点なのでタイル不要）
  if (adjacentHex.city) {
    debug.log(`[isEdgeConnected] ${coordToKey(coord)} dir=${direction} → ${coordToKey(adjacentCoord)} = CITY (${adjacentHex.city.name})`);
    return true;
  }

  // 町（Town）ヘクスはタイルが配置されて初めて接続が成立する
  // タイルがない町ヘクスは接続先ではない（下の tile チェックで判定）

  // タイルがあり、対応する辺に接続があれば接続済み
  if (adjacentHex.tile) {
    const oppositeDir = getOppositeDirection(direction);
    const tileDef = getTileDefinition(adjacentHex.tile.type);
    if (tileDef) {
      // 回転を適用した接続を確認
      for (const conn of tileDef.connections) {
        const rotatedConn = conn.map(edge => (edge + adjacentHex.tile!.rotation) % 6);
        if (rotatedConn.includes(oppositeDir)) {
          debug.log(`[isEdgeConnected] ${coordToKey(coord)} dir=${direction} → ${coordToKey(adjacentCoord)} = TILE ${adjacentHex.tile.type} connects at dir=${oppositeDir}`);
          return true;
        }
      }
    }
    debug.log(`[isEdgeConnected] ${coordToKey(coord)} dir=${direction} → ${coordToKey(adjacentCoord)} = TILE ${adjacentHex.tile.type} but no connection at dir=${oppositeDir}`);
  }

  debug.log(`[isEdgeConnected] ${coordToKey(coord)} dir=${direction} → ${coordToKey(adjacentCoord)} = EMPTY`);
  return false;
}

/**
 * タイルの開放端を検出
 *
 * @param G - ゲーム状態
 * @param coord - タイル座標
 * @param tile - 配置されたタイル
 * @returns 開放端情報の配列
 */
export function detectOpenEnds(
  G: AoSGameState,
  coord: HexCoordinate,
  tile: TrackTile
): OpenEndInfo[] {
  const openEnds: OpenEndInfo[] = [];
  const tileDef = getTileDefinition(tile.type);

  if (!tileDef) {
    console.warn(`[detectOpenEnds] Unknown tile type: ${tile.type}`);
    return openEnds;
  }

  debug.log(`[detectOpenEnds] Checking tile ${tile.type} at ${coordToKey(coord)}, rotation=${tile.rotation}, base connections=${JSON.stringify(tileDef.connections)}`);

  // Issue #30: Town タイルは単一辺接続として特別処理
  // Town タイルの connections は [[0], [2], [4]] のような形式（各辺が中心に接続）
  if (tileDef.isTown) {
    debug.log(`[detectOpenEnds] Town tile detected - checking each edge individually`);
    for (let i = 0; i < tileDef.connections.length; i++) {
      const conn = tileDef.connections[i];
      if (conn.length !== 1) continue; // 単一辺接続のみ処理

      const edge = ((conn[0] + tile.rotation) % 6) as Direction;
      const edgeConnected = isEdgeConnected(G, coord, edge);

      debug.log(`[detectOpenEnds] Town edge ${i}: edge=${edge}, connected=${edgeConnected}`);

      if (!edgeConnected) {
        // 未接続の辺 → UnfinishedSection として登録
        // Town タイルは終点なので entryEdge = exitEdge（町で終端）
        debug.log(`[detectOpenEnds] → Town open end detected: edge=${edge}`);
        openEnds.push({
          entryEdge: edge,
          exitEdge: edge, // Town は終点なので同じ辺
          connectionIndex: i
        });
      }
    }
    debug.log(`[detectOpenEnds] Total open ends found: ${openEnds.length}`);
    return openEnds;
  }

  // 通常タイル: 各接続ペアについて開放端を検出
  for (let i = 0; i < tileDef.connections.length; i++) {
    const conn = tileDef.connections[i];
    // 回転を適用
    const [edge1, edge2] = conn.map(edge => ((edge + tile.rotation) % 6) as Direction);

    const edge1Connected = isEdgeConnected(G, coord, edge1);
    const edge2Connected = isEdgeConnected(G, coord, edge2);

    debug.log(`[detectOpenEnds] Path ${i}: edges [${edge1}, ${edge2}], connected: [${edge1Connected}, ${edge2Connected}]`);

    if (edge1Connected && !edge2Connected) {
      // edge1 が接続済み、edge2 が開放端
      debug.log(`[detectOpenEnds] → Open end detected: entry=${edge1}, exit=${edge2}`);
      openEnds.push({
        entryEdge: edge1,
        exitEdge: edge2,
        connectionIndex: tileDef.connections.length > 1 ? i : undefined
      });
    } else if (!edge1Connected && edge2Connected) {
      // edge2 が接続済み、edge1 が開放端
      debug.log(`[detectOpenEnds] → Open end detected: entry=${edge2}, exit=${edge1}`);
      openEnds.push({
        entryEdge: edge2,
        exitEdge: edge1,
        connectionIndex: tileDef.connections.length > 1 ? i : undefined
      });
    } else if (!edge1Connected && !edge2Connected) {
      // 両端とも開放（例: 独立したタイル）
      // Issue #32: Town ヘクス上のタイルでは、Town が接続点として機能する
      // 両端開放でも Town 経由でネットワークに接続されているため、両端を登録
      if (tile.hasTownDisk) {
        debug.log(`[detectOpenEnds] → Both edges open on Town hex: registering both edges`);
        // edge1 側の開放端
        openEnds.push({
          entryEdge: edge2, // Town 経由で入る（論理的な入口）
          exitEdge: edge1,
          connectionIndex: tileDef.connections.length > 1 ? i : undefined
        });
        // edge2 側の開放端
        openEnds.push({
          entryEdge: edge1, // Town 経由で入る（論理的な入口）
          exitEdge: edge2,
          connectionIndex: tileDef.connections.length > 1 ? i : undefined
        });
      } else {
        // 通常タイル: 両端開放は UnfinishedSection として扱わない（都市/町に接続していないため）
        debug.log(`[detectOpenEnds] → Both edges open (floating), skipping`);
      }
    } else {
      // 両端とも接続済みの場合は開放端なし
      debug.log(`[detectOpenEnds] → Both edges connected (complete), skipping`);
    }
  }

  debug.log(`[detectOpenEnds] Total open ends found: ${openEnds.length}`);
  return openEnds;
}

/**
 * UnfinishedSection ID生成
 */
function generateSectionId(): string {
  return `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * UnfinishedSections を更新（タイル配置時）
 *
 * 新しいタイルを配置した際に:
 * 1. 既存の開放端が接続されたら削除
 * 2. 新しい開放端があれば追加
 *
 * @param G - ゲーム状態
 * @param coord - 配置座標
 * @param tile - 配置されたタイル
 * @param playerId - 配置プレイヤーID
 * @param isDirectionChange - 方向転換（Replace パターン2,3）の場合 true。
 *                            方向転換は線路追加ではないため tilesAddedSinceLastTurn を加算しない。
 */
export function updateUnfinishedSections(
  G: AoSGameState,
  coord: HexCoordinate,
  tile: TrackTile,
  playerId: PlayerId,
  isDirectionChange: boolean = false
): void {
  const tileDef = getTileDefinition(tile.type);
  if (!tileDef) return;

  // 回転を適用した接続方向を取得
  const rotatedConnections = tileDef.connections.map(conn =>
    conn.map(edge => ((edge + tile.rotation) % 6) as Direction)
  );

  // 0. 配置座標の既存 UnfinishedSection を削除（Replace 時に古いセクションが残らないようにする）
  const coordKey = coordToKey(coord);
  const beforeCount = G.unfinishedSections.length;
  G.unfinishedSections = G.unfinishedSections.filter(section => {
    if (coordToKey(section.coord) === coordKey) {
      debug.log(`[updateUnfinishedSections] Removed existing section at ${coordKey}, entry=${section.entryEdge}, exit=${section.exitEdge}`);
      return false;
    }
    return true;
  });
  if (beforeCount !== G.unfinishedSections.length) {
    debug.log(`[updateUnfinishedSections] Cleared ${beforeCount - G.unfinishedSections.length} existing sections at ${coordKey}`);
  }

  // 1. 配置タイルが接続する方向について、隣接タイルの開放端を削除
  for (const conn of rotatedConnections) {
    for (const direction of conn) {
      removeConnectedOpenEnds(G, coord, direction);
    }
  }

  // 2. 新しい開放端を検出して追加
  const openEnds = detectOpenEnds(G, coord, tile);

  for (const openEnd of openEnds) {
    // 方向転換の場合は tilesAddedSinceLastTurn を 0 に設定
    // （線路追加ではないため、手番終了時に所有権が失われる）
    const tilesAdded = isDirectionChange ? 0 : 1;

    const newSection: UnfinishedSection = {
      id: generateSectionId(),
      coord: coord,
      entryEdge: openEnd.entryEdge,
      exitEdge: openEnd.exitEdge,
      owner: playerId,
      connectionIndex: openEnd.connectionIndex,
      createdTurn: 1, // TODO: G.currentTurn から取得
      tilesAddedSinceLastTurn: tilesAdded
    };

    G.unfinishedSections.push(newSection);
    debug.log(`[updateUnfinishedSections] Added section: coord=${coordToKey(coord)}, entry=${openEnd.entryEdge}, exit=${openEnd.exitEdge}, tilesAdded=${tilesAdded}${isDirectionChange ? ' (direction change)' : ''}`);
  }
}

/**
 * 接続された開放端を削除
 *
 * タイル配置によって隣接ヘクスの開放端が接続された場合、
 * その開放端を UnfinishedSections から削除する。
 *
 * @param G - ゲーム状態
 * @param newTileCoord - 新しく配置されたタイルの座標
 * @param direction - 新しいタイルから見た接続方向
 */
export function removeConnectedOpenEnds(
  G: AoSGameState,
  newTileCoord: HexCoordinate,
  direction: Direction
): void {
  const adjacentCoord = getAdjacentHex(newTileCoord, direction);
  const adjacentKey = coordToKey(adjacentCoord);
  const oppositeDir = getOppositeDirection(direction);

  // 隣接座標にある、かつ出口が新タイル方向を向いている UnfinishedSection を削除
  const beforeCount = G.unfinishedSections.length;
  G.unfinishedSections = G.unfinishedSections.filter(section => {
    if (coordToKey(section.coord) === adjacentKey && section.exitEdge === oppositeDir) {
      debug.log(`[removeConnectedOpenEnds] Removed section at ${adjacentKey}, exit=${oppositeDir} (connected by new tile at ${coordToKey(newTileCoord)})`);
      return false;
    }
    return true;
  });

  if (beforeCount !== G.unfinishedSections.length) {
    debug.log(`[removeConnectedOpenEnds] Removed ${beforeCount - G.unfinishedSections.length} sections`);
  }
}

/**
 * 指定座標の UnfinishedSection を取得
 *
 * redirect 対象判定に使用。
 *
 * @param G - ゲーム状態
 * @param coord - 座標
 * @returns 該当する UnfinishedSection の配列
 */
export function getUnfinishedSectionsAt(
  G: AoSGameState,
  coord: HexCoordinate
): UnfinishedSection[] {
  const key = coordToKey(coord);
  return G.unfinishedSections.filter(section => coordToKey(section.coord) === key);
}

/**
 * リンク完成時に該当する UnfinishedSection を削除
 *
 * @param G - ゲーム状態
 * @param linkPath - 完成したリンクの経路
 */
export function removeSectionsForCompletedLink(
  G: AoSGameState,
  linkPath: HexCoordinate[]
): void {
  const pathKeys = new Set(linkPath.map(coord => coordToKey(coord)));

  const beforeCount = G.unfinishedSections.length;
  G.unfinishedSections = G.unfinishedSections.filter(section => {
    const sectionKey = coordToKey(section.coord);

    if (pathKeys.has(sectionKey)) {
      // この座標のタイルが複線タイルかチェック
      const hex = G.map.find(h => coordToKey(h.coordinate) === sectionKey);
      if (hex?.tile) {
        const tileDef = getTileDefinition(hex.tile.type);
        // connections が2ペア以上なら複線タイル
        if (tileDef && tileDef.connections.length >= 2) {
          // 複線タイルの場合、完成した経路に使われた connectionIndex のみ削除
          // TODO: pathSegments から使用された connectionIndex を特定する
          // 暫定: 複線タイルは削除しない（もう1本が未完成の可能性）
          debug.log(`[removeSectionsForCompletedLink] Keeping section for multi-track tile at ${sectionKey}`);
          return true;
        }
      }
      debug.log(`[removeSectionsForCompletedLink] Removed section at ${sectionKey}`);
      return false;
    }
    return true;
  });

  const removedCount = beforeCount - G.unfinishedSections.length;
  if (removedCount > 0) {
    debug.log(`[removeSectionsForCompletedLink] Removed ${removedCount} sections`);
  }
}

/**
 * Town 都市化時に該当座標の UnfinishedSection を削除
 *
 * Issue #33: 新都市タイルが配置され Town が都市化された場合、
 * その座標の UnfinishedSections をすべて削除する。
 * 都市は終端点なので、開放端（UnfinishedSection）にはならない。
 *
 * @param G - ゲーム状態
 * @param coord - 都市化された Town の座標
 */
export function clearSectionsAtUrbanizedTown(
  G: AoSGameState,
  coord: HexCoordinate
): void {
  const coordKey = coordToKey(coord);
  const beforeCount = G.unfinishedSections.length;

  G.unfinishedSections = G.unfinishedSections.filter(section => {
    if (coordToKey(section.coord) === coordKey) {
      debug.log(`[clearSectionsAtUrbanizedTown] Removed section at ${coordKey}, entry=${section.entryEdge}, exit=${section.exitEdge}`);
      return false;
    }
    return true;
  });

  const removedCount = beforeCount - G.unfinishedSections.length;
  if (removedCount > 0) {
    debug.log(`[clearSectionsAtUrbanizedTown] Removed ${removedCount} sections at urbanized Town ${coordKey}`);
  }
}
